import FinancingType = App.Enums.EstateObject.Loans.FinancingType

export type LoanFieldKey =
  | 'lender_name'
  | 'loan_type'
  | 'principal_amount'
  | 'mortgage'
  | 'final_payment_amount'
  | 'start_date'
  | 'repayment_start_date'
  | 'exit_date'
  | 'fixed_interest_rate_term'
  | 'loan_term'
  | 'nominal_interest_rate'
  | 'initial_repayment_rate'
  | 'mezzanine_type'

export type LoanFieldKind = 'text' | 'select' | 'currency' | 'percent' | 'years' | 'date'

export interface LoanField {
  key: LoanFieldKey
  kind: LoanFieldKind
}

export interface LoanSection {
  key: string
  fields: LoanField[]
}

const SECTIONS: LoanSection[] = [
  {
    key: 'basics',
    fields: [
      { key: 'lender_name', kind: 'text' },
      { key: 'loan_type', kind: 'select' },
    ],
  },
  {
    key: 'amount',
    fields: [
      { key: 'principal_amount', kind: 'currency' },
      { key: 'mortgage', kind: 'currency' },
      { key: 'final_payment_amount', kind: 'currency' },
    ],
  },
  {
    key: 'schedule',
    fields: [
      { key: 'start_date', kind: 'date' },
      { key: 'repayment_start_date', kind: 'date' },
      { key: 'fixed_interest_rate_term', kind: 'years' },
      { key: 'loan_term', kind: 'years' },
      { key: 'exit_date', kind: 'date' },
    ],
  },
  {
    key: 'interest',
    fields: [
      { key: 'nominal_interest_rate', kind: 'percent' },
      { key: 'initial_repayment_rate', kind: 'percent' },
    ],
  },
  {
    key: 'mezzanine',
    fields: [{ key: 'mezzanine_type', kind: 'select' }],
  },
]

const COMMON_FIELDS: LoanFieldKey[] = ['lender_name', 'loan_type']

const AMORTIZING_FIELDS: LoanFieldKey[] = [
  'principal_amount',
  'mortgage',
  'start_date',
  'repayment_start_date',
  'fixed_interest_rate_term',
  'loan_term',
  'nominal_interest_rate',
  'initial_repayment_rate',
]

const FIELDS_PER_LOAN_TYPE: Record<FinancingType, LoanFieldKey[]> = {
  annuity_loan: AMORTIZING_FIELDS,
  kfw_loan: AMORTIZING_FIELDS,
  modernization_loan: AMORTIZING_FIELDS,
  forward_loan: AMORTIZING_FIELDS,
  variable_loan: ['principal_amount', 'mortgage', 'start_date', 'initial_repayment_rate'],
  building_society_loan: [
    'principal_amount',
    'start_date',
    'repayment_start_date',
    'loan_term',
    'nominal_interest_rate',
    'initial_repayment_rate',
  ],
  bullet_loan: [
    'principal_amount',
    'mortgage',
    'final_payment_amount',
    'start_date',
    'fixed_interest_rate_term',
    'loan_term',
    'nominal_interest_rate',
  ],
  mezzanine_capital: [
    'principal_amount',
    'final_payment_amount',
    'start_date',
    'exit_date',
    'loan_term',
    'nominal_interest_rate',
    'mezzanine_type',
  ],
}

function visibleFieldKeys(loanType: FinancingType | null): LoanFieldKey[] {
  return [...COMMON_FIELDS, ...(loanType ? FIELDS_PER_LOAN_TYPE[loanType] : [])]
}

export function loanSections(loanType: FinancingType | null): LoanSection[] {
  const visible = visibleFieldKeys(loanType)

  return SECTIONS.map((section) => ({
    ...section,
    fields: section.fields.filter((field) => visible.includes(field.key)),
  })).filter((section) => section.fields.length > 0)
}

export function hasAnnuity(loanType: FinancingType | null): boolean {
  const visible = visibleFieldKeys(loanType)

  return visible.includes('nominal_interest_rate') && visible.includes('initial_repayment_rate')
}

const REPAYMENT_RATE_FRACTION_DIGITS = 2

export function yearlyAnnuity(
  principal: number | null,
  interestRate: number | null,
  repaymentRate: number | null,
): number | null {
  if (!principal || repaymentRate === null) {
    return null
  }

  return (principal * ((interestRate ?? 0) + repaymentRate)) / 100
}

export function monthlyAnnuity(
  principal: number | null,
  interestRate: number | null,
  repaymentRate: number | null,
): number | null {
  const yearly = yearlyAnnuity(principal, interestRate, repaymentRate)

  return yearly === null ? null : yearly / 12
}

export function repaymentRateFromYearlyAnnuity(
  principal: number | null,
  interestRate: number | null,
  yearly: number | null,
): number | null {
  if (!principal || yearly === null) {
    return null
  }

  const rate = (yearly / principal) * 100 - (interestRate ?? 0)

  return Number(Math.max(0, rate).toFixed(REPAYMENT_RATE_FRACTION_DIGITS))
}
